'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useLocale } from 'next-intl';
import { useLocalizedPath } from '@/src/hooks/useLocalizedPath';
import { useAuth } from '@/src/context/AuthContext';
import { useCart } from '@/src/context/CartContext';
import { useTurnstile } from '@/src/hooks/useTurnstile';
import GoogleSignInButton from './GoogleSignInButton';
import FacebookSignInButton from './FacebookSignInButton';
import GithubSignInButton from './GithubSignInButton';
import TwitterSignInButton from './TwitterSignInButton';
import { resolveErrorMessage } from '@/src/utils/errorInterpolate';
import { readReferralCookie } from '@/src/lib/referral';
import styles from './Auth.module.css';

interface LoginFormProps {
  translations: {
    title: string;
    subtitle: string;
    emailLabel: string;
    emailPlaceholder: string;
    passwordLabel: string;
    passwordPlaceholder: string;
    submit: string;
    submitting: string;
    forgotPassword: string;
    noAccount: string;
    register: string;
    orDivider: string;
    googleSignIn: string;
    facebookSignIn: string;
    githubSignIn: string;
    twitterSignIn: string;
    twoFactor: {
      title: string;
      subtitle: string;
      codeLabel: string;
      codePlaceholder: string;
      submit: string;
      submitting: string;
      back: string;
    };
    errors: Record<string, string>;
  };
}

const LoginForm: React.FC<LoginFormProps> = ({ translations }) => {
  const router = useRouter();
  const locale = useLocale();
  const { localizeFullPath } = useLocalizedPath();
  const { login, verifyTwoFactor } = useAuth();
  const { mergeCartOnLogin } = useCart();
  const turnstile = useTurnstile({ action: 'login' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [pending2faToken, setPending2faToken] = useState<string | null>(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [referral, setReferral] = useState<string | undefined>(undefined);

  useEffect(() => {
    setReferral(readReferralCookie());
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError(null);
  };

  const finishLoginRedirect = async () => {
    // Merge any guest cart from this browser with the user's server-side
    // pending carts. Non-throwing - never blocks the redirect.
    await mergeCartOnLogin();
    router.push(localizeFullPath('/'));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (turnstile.isRequired && !turnstile.token) {
      setError(translations.errors.DEFAULT);
      return;
    }
    setIsSubmitting(true);
    setError(null);

    try {
      const session = await login({
        email: formData.email,
        password: formData.password,
        locale,
        turnstileToken: turnstile.token ?? undefined,
      });
      if (session?.requires2FA && session.pending2faToken) {
        setPending2faToken(session.pending2faToken);
        return;
      }
      await finishLoginRedirect();
    } catch (err) {
      turnstile.reset();
      setError(resolveErrorMessage(err, translations.errors));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTwoFactorSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pending2faToken) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await verifyTwoFactor(pending2faToken, twoFactorCode.trim());
      await finishLoginRedirect();
    } catch (err) {
      setError(resolveErrorMessage(err, translations.errors));
    } finally {
      setIsSubmitting(false);
    }
  };

  const backToCredentials = () => {
    setPending2faToken(null);
    setTwoFactorCode('');
    setError(null);
  };

  if (pending2faToken) {
    return (
      <div className={styles.authPage}>
        <div className={styles.authCard}>
          <div className={styles.authLogo}>
            <Link href="/">
              <img src="/short-logo-black.svg" alt="ACEB" />
            </Link>
          </div>
          <h1 className={styles.authTitle}>{translations.twoFactor.title}</h1>
          <p className={styles.authSubtitle}>{translations.twoFactor.subtitle}</p>

          {error && (
            <div className={`${styles.alert} ${styles.alertError}`}>{error}</div>
          )}

          <form className={styles.authForm} onSubmit={handleTwoFactorSubmit}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel} htmlFor="login-2fa-code">
                {translations.twoFactor.codeLabel}
              </label>
              <input
                id="login-2fa-code"
                className={styles.formInput}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                placeholder={translations.twoFactor.codePlaceholder}
                value={twoFactorCode}
                onChange={(e) => {
                  setTwoFactorCode(e.target.value);
                  if (error) setError(null);
                }}
                required
              />
            </div>

            <button type="submit" className={styles.submitButton} disabled={isSubmitting || !twoFactorCode}>
              {isSubmitting ? (
                <>
                  <span className={styles.spinner} />
                  {translations.twoFactor.submitting}
                </>
              ) : (
                translations.twoFactor.submit
              )}
            </button>
          </form>

          <p className={styles.authFooter}>
            <button
              type="button"
              onClick={backToCredentials}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                color: 'inherit',
                textDecoration: 'underline',
                font: 'inherit',
              }}
            >
              {translations.twoFactor.back}
            </button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.authPage}>
      <div className={styles.authCard}>
        <div className={styles.authLogo}>
          <Link href="/">
            <img src="/short-logo-black.svg" alt="ACEB" />
          </Link>
        </div>
        <h1 className={styles.authTitle}>{translations.title}</h1>
        <p className={styles.authSubtitle}>{translations.subtitle}</p>

        {error && (
          <div className={`${styles.alert} ${styles.alertError}`}>{error}</div>
        )}

        <form className={styles.authForm} onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel} htmlFor="login-email">
              {translations.emailLabel}
            </label>
            <input
              id="login-email"
              className={styles.formInput}
              type="email"
              name="email"
              placeholder={translations.emailPlaceholder}
              value={formData.email}
              onChange={handleChange}
              autoComplete="email"
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel} htmlFor="login-password">
              {translations.passwordLabel}
            </label>
            <div className={styles.passwordToggle}>
              <input
                id="login-password"
                className={styles.formInput}
                type={showPassword ? 'text' : 'password'}
                name="password"
                placeholder={translations.passwordPlaceholder}
                value={formData.password}
                onChange={handleChange}
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                className={styles.passwordToggleButton}
                onClick={() => setShowPassword(prev => !prev)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div className={styles.forgotPassword}>
            <Link href={localizeFullPath('/forgot-password')}>{translations.forgotPassword}</Link>
          </div>

          {turnstile.widget}

          <button
            type="submit"
            className={styles.submitButton}
            disabled={isSubmitting || (turnstile.isRequired && !turnstile.token)}
          >
            {isSubmitting ? (
              <>
                <span className={styles.spinner} />
                {translations.submitting}
              </>
            ) : (
              translations.submit
            )}
          </button>
        </form>

        <div className={styles.divider}>{translations.orDivider}</div>

        <GoogleSignInButton label={translations.googleSignIn} referral={referral} disabled={isSubmitting} />
        <FacebookSignInButton label={translations.facebookSignIn} referral={referral} disabled={isSubmitting} />
        <GithubSignInButton label={translations.githubSignIn} referral={referral} disabled={isSubmitting} />
        <TwitterSignInButton label={translations.twitterSignIn} referral={referral} disabled={isSubmitting} />

        <p className={styles.authFooter}>
          {translations.noAccount}{' '}
          <Link href={localizeFullPath('/register')}>{translations.register}</Link>
        </p>
      </div>
    </div>
  );
};

export default LoginForm;
